import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { Sidebar } from './Sidebar';
import WallAreaFactorsCard from './WallAreaFactorsCard';
import { calcWood, calcSteel } from './calculator';
import { requiredCodebordRatio } from './climateCompliance';
import { cavityChoicesWood, cavityChoicesSteel, toRSI } from './constants';
import generateResultsPdf from './pdfResults';
import { connectLocation } from '../../../../location/LocationConnectors';
import { useTRC } from '../../store';
import CalculatorLayout from '../shared/CalculatorLayout';
import Footer from '../shared/Footer';
import ResultsBlock from '../shared/ResultsBlock';
import { insulationOptions, resetResults, validateInputs } from '../shared/constants';
import { smoothScrollIntoView } from '../shared/scroll';

/* ----------------------------- Defaults ----------------------------- */

const DEFAULTS = {
  // selector for wood/steel
  studsKind: 'wood',

  // wood
  woodSize: '',
  spacingWood: '',
  cavityRSIWood: '',

  // steel
  steelSize: '2x4',
  steelSpacingGroup: '<500',
  cavityRSISteel: '',

  // shared continuous layers
  cladding: 'brickClay102',
  hasBrickAirSpace: null,
  hardieThickness: '',
  hasRainScreen: true,
  rainScreenAirGap: '13-19',

  sheathingType: '',
  osbThickness: '9.5',
  gwbSheathThickness: '12.7',
  codebordSheathThickness: '',

  hasWeatherBarrier: true,
  hasVapourBarrier: true,

  interiorGWB: '12.7',
  woodStrapBehindGWB: true,

  hasExteriorInsulation: true,
  exteriorInsulationType: '',
  exteriorCodebordInches: '',
  exteriorRainbarrierInches: '',
};

// TODO: state variables could be a single object to reduce verbosity and the amout of setState calls
const WallsAboveGrade = ({ calcId, t }) => {
  const { state, setResults: publishResults, setInputs: publishInputs } = useTRC();
  const { province, region, hdd } = state.location;
  const hasLocation = !!(province && region);

  const rsiOptionsSteel = insulationOptions(cavityChoicesSteel, toRSI);
  const rsiOptionsWood = insulationOptions(cavityChoicesWood, toRSI);

  // -------- hydrate inputs from store or defaults
  const savedInputs = state.inputsByCalc?.[calcId] || {};

  const [studsKind, setStudsKind] = useState(savedInputs.studsKind ?? DEFAULTS.studsKind);

  // wood
  const [woodSize, setWoodSize] = useState(savedInputs.woodSize ?? DEFAULTS.woodSize);
  const [spacingWood, setSpacingWood] = useState(savedInputs.spacingWood ?? DEFAULTS.spacingWood);
  const [cavityRSIWood, setCavityRSIWood] = useState(savedInputs.cavityRSIWood ?? DEFAULTS.cavityRSIWood);

  // steel
  const [steelSize, setSteelSize] = useState(savedInputs.steelSize ?? DEFAULTS.steelSize);
  // eslint-disable-next-line max-len
  const [steelSpacingGroup, setSteelSpacingGroup] = useState(
    savedInputs.steelSpacingGroup ?? DEFAULTS.steelSpacingGroup
  );
  const [cavityRSISteel, setCavityRSISteel] = useState(savedInputs.cavityRSISteel ?? DEFAULTS.cavityRSISteel);

  // shared continuous layers
  const [cladding, setCladding] = useState(savedInputs.cladding ?? DEFAULTS.cladding);
  const [hasBrickAirSpace, setHasBrickAirSpace] = useState(savedInputs.hasBrickAirSpace ?? DEFAULTS.hasBrickAirSpace);
  const [hardieThickness, setHardieThickness] = useState(savedInputs.hardieThickness ?? DEFAULTS.hardieThickness);
  const [hasRainScreen, setHasRainScreen] = useState(savedInputs.hasRainScreen ?? DEFAULTS.hasRainScreen);
  const [rainScreenAirGap, setRainScreenAirGap] = useState(savedInputs.rainScreenAirGap ?? DEFAULTS.rainScreenAirGap);

  const [sheathingType, setSheathingType] = useState(savedInputs.sheathingType ?? DEFAULTS.sheathingType);
  const [osbThickness, setOsbThickness] = useState(savedInputs.osbThickness ?? DEFAULTS.osbThickness);
  const [gwbSheathThickness, setGwbSheathThickness] = useState(
    savedInputs.gwbSheathThickness ?? DEFAULTS.gwbSheathThickness
  );

  const [codebordSheathThickness, setCodebordSheathThickness] = useState(
    savedInputs.codebordSheathThickness ?? DEFAULTS.codebordSheathThickness
  );

  const [hasWeatherBarrier, setHasWeatherBarrier] = useState(
    savedInputs.hasWeatherBarrier ?? DEFAULTS.hasWeatherBarrier
  );
  const [hasVapourBarrier, setHasVapourBarrier] = useState(savedInputs.hasVapourBarrier ?? DEFAULTS.hasVapourBarrier);

  const [interiorGWB, setInteriorGWB] = useState(savedInputs.interiorGWB ?? DEFAULTS.interiorGWB);
  const [woodStrapBehindGWB, setWoodStrapBehindGWB] = useState(
    savedInputs.woodStrapBehindGWB ?? DEFAULTS.woodStrapBehindGWB
  );

  const [hasExteriorInsulation, setHasExteriorInsulation] = useState(
    savedInputs.hasExteriorInsulation ?? DEFAULTS.hasExteriorInsulation
  );

  const [exteriorInsulationType, setExteriorInsulationType] = useState(
    savedInputs.exteriorInsulationType ?? DEFAULTS.exteriorInsulationType
  );

  const [exteriorCodebordInches, setExteriorCodebordInches] = useState(
    savedInputs.exteriorCodebordInches ?? DEFAULTS.exteriorCodebordInches
  );

  const [exteriorRainbarrierInches, setExteriorRainbarrierInches] = useState(
    savedInputs.exteriorRainbarrierInches ?? DEFAULTS.exteriorRainbarrierInches
  );

  const resultsRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    publishInputs({
      calcId,
      inputs: {
        studsKind,
        // wood
        woodSize,
        spacingWood,
        cavityRSIWood,
        // steel
        steelSize,
        steelSpacingGroup,
        cavityRSISteel,
        // shared continuous
        cladding,
        hasBrickAirSpace,
        hardieThickness,
        hasRainScreen,
        rainScreenAirGap,
        sheathingType,
        osbThickness,
        gwbSheathThickness,
        codebordSheathThickness,
        hasWeatherBarrier,
        hasVapourBarrier,
        interiorGWB,
        woodStrapBehindGWB,
        hasExteriorInsulation,
        exteriorCodebordInches,
        exteriorInsulationType,
        exteriorRainbarrierInches,
      },
    });
  }, [
    calcId,
    studsKind,
    woodSize,
    spacingWood,
    cavityRSIWood,
    steelSize,
    steelSpacingGroup,
    cavityRSISteel,
    cladding,
    hasBrickAirSpace,
    hardieThickness,
    hasRainScreen,
    rainScreenAirGap,
    sheathingType,
    osbThickness,
    gwbSheathThickness,
    codebordSheathThickness,
    hasWeatherBarrier,
    hasVapourBarrier,
    interiorGWB,
    woodStrapBehindGWB,
    hasExteriorInsulation,
    exteriorCodebordInches,
    exteriorInsulationType,
    exteriorRainbarrierInches,
    publishInputs,
  ]);

  // Dependent resets (match your original logic)
  useEffect(() => {
    if (sheathingType === 'osb') {
      setOsbThickness('9.5');
    } else if (sheathingType === 'gwb') {
      setGwbSheathThickness('12.7');
    } else if (sheathingType === 'codebord' && !codebordSheathThickness) {
      setCodebordSheathThickness('1');
    }
  }, [sheathingType]);

  useEffect(() => {
    // Air space:
    // - brickClay102: default to true only if user hasn't chosen yet (null/undefined)
    // - all other claddings: force to false
    if (!cladding) {
      setHasBrickAirSpace(true);
    } else {
      setHasBrickAirSpace(false);
    }

    // Hardie thickness:
    // - hardieOverStrap: keep existing choice if present, otherwise default to '6.35'
    // - all other claddings: clear to ''
    setHardieThickness(prev => (cladding === 'hardieOverStrap' ? prev || '6.35' : ''));
  }, [cladding]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [error]);

  // Read results from the store so they persist across tab switches
  const results = state.resultsByCalc?.[calcId]?.results || null;

  const woodRows = [
    { label: t('thermal_calculator.effective_assembly_rsi'), rsi: 'effectiveAssemblyRSI' },
    { label: t('thermal_calculator.center_of_cavity'), rsi: 'centerOfCavityRSI' },
    { label: t('thermal_calculator.installed_insulation_rsi'), rsi: 'installedInsulationRSI', roundR: true },
  ];

  const steelRows = [
    { label: t('thermal_calculator.effective_assembly_rsi'), rsi: 'effectiveAssemblyRSI' },
    { label: t('thermal_calculator.center_of_cavity'), rsi: 'centerOfCavityRSI' },
    { label: t('thermal_calculator.installed_insulation_rsi'), rsi: 'installedInsulationRSI' },
    { label: 'RSIt1', rsi: 'RSIt1' },
    { label: 'RSIt2', rsi: 'RSIt2' },
    { label: 'RSIt3', rsi: 'RSIt3' },
    { label: 'K1', rsi: r => r?.K1 ?? null, noR: true },
    { label: 'K2', rsi: r => r?.K2 ?? null, noR: true },
  ];

  const baseRows = studsKind === 'steel' ? steelRows : woodRows;

  const hasLowPermCodebord = results?.lowPermAtExterior;

  const requiredRatio = hasLowPermCodebord ? requiredCodebordRatio(hdd, true) : 0;

  // Add the ratio/compliance rows only when CodeBord is present (sheet behavior)
  const extraRows = [
    {
      label: t('thermal_calculator.walls_above_grade.outboard_inboard_ratio_codebord'),
      rsi: r => r?.outboardInboardRatio ?? null,
      text: hasLowPermCodebord ? null : () => 'N/A',
      noR: true,
    },
    {
      label: `${t('thermal_calculator.walls_above_grade.minimum_ratio')} (HDD ${hdd})`,
      rsi: () => requiredRatio,
      noR: true,
    },
    {
      label: `${t('thermal_calculator.walls_above_grade.compliant_with_table')} A-9.25.5.2`,
      rsi: () => null, // satisfy PropTypes; won’t be used
      text: r => {
        const codebordRatioCompliant = r?.outboardInboardRatio >= requiredRatio;
        if (typeof codebordRatioCompliant !== 'boolean' || !hasLowPermCodebord) return 'N/A';
        return codebordRatioCompliant ? t('thermal_calculator.answer_yes') : t('thermal_calculator.answer_no');
      },
      noR: true,
    },
  ];

  const rowsForTable = [...baseRows, ...extraRows];

  const tableTitle =
    studsKind === 'steel'
      ? t('thermal_calculator.walls_above_grade.steel_studs')
      : t('thermal_calculator.walls_above_grade.wood_studs');

  // hydrate the shared calculator input shape
  const sharedState = {
    cladding,
    hasBrickAirSpace,
    hasRainScreen,
    rainScreenAirGap,
    sheathingType,
    osbThickness,
    gwbSheathThickness,
    codebordSheathThickness,
    hasWeatherBarrier,
    hasVapourBarrier,
    interiorGWB,
    woodStrapBehindGWB,
    hasExteriorInsulation,
    exteriorInsulationType,
    exteriorCodebordInches,
    exteriorRainbarrierInches,
  };

  const onCalculate = () => {
    const cavityRSI = studsKind === 'wood' ? cavityRSIWood : cavityRSISteel;
    if (!validateInputs({ province, region, setError, cavityRSI, t })) {
      // do not change persisted inputs; just report error
      smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
      return;
    }
    // inputs are valid, clear any previous error
    setError(null);

    const res =
      studsKind === 'steel'
        ? calcSteel({ ...sharedState, steelSpacingGroup, steelSize, cavityRSISteel }, t)
        : calcWood({ ...sharedState, spacingWood, woodSize, cavityRSIWood }, t);

    publishResults({
      calcId,
      results: res,
      meta: {
        title: `${t('thermal_calculator.titles.walls_above_grade')} - (${tableTitle.toLowerCase()})`,
      },
    });

    smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
  };

  const onReset = () => {
    // clear results in store
    resetResults(calcId, t('thermal_calculator.titles.walls_above_grade'), publishResults);

    // reset local inputs
    setStudsKind(DEFAULTS.studsKind);
    setWoodSize(DEFAULTS.woodSize);
    setSpacingWood(DEFAULTS.spacingWood);
    setCavityRSIWood(DEFAULTS.cavityRSIWood);

    setSteelSize(DEFAULTS.steelSize);
    setSteelSpacingGroup(DEFAULTS.steelSpacingGroup);
    setCavityRSISteel(DEFAULTS.cavityRSISteel);

    setCladding(DEFAULTS.cladding);
    setHasBrickAirSpace(DEFAULTS.hasBrickAirSpace);
    setHardieThickness(DEFAULTS.hardieThickness);
    setHasRainScreen(DEFAULTS.hasRainScreen);
    setRainScreenAirGap(DEFAULTS.rainScreenAirGap);

    setSheathingType(DEFAULTS.sheathingType);
    setOsbThickness(DEFAULTS.osbThickness);
    setGwbSheathThickness(DEFAULTS.gwbSheathThickness);
    setCodebordSheathThickness(DEFAULTS.codebordSheathThickness);

    setHasWeatherBarrier(DEFAULTS.hasWeatherBarrier);
    setHasVapourBarrier(DEFAULTS.hasVapourBarrier);

    setInteriorGWB(DEFAULTS.interiorGWB);
    setWoodStrapBehindGWB(DEFAULTS.woodStrapBehindGWB);

    setHasExteriorInsulation(DEFAULTS.hasExteriorInsulation);
    setExteriorInsulationType(DEFAULTS.exteriorInsulationType);
    setExteriorCodebordInches(DEFAULTS.exteriorCodebordInches);
    setExteriorRainbarrierInches(DEFAULTS.exteriorRainbarrierInches);

    setError(null);

    // clear inputs in store for this calc
    publishInputs({ calcId, inputs: null });
  };

  /* ------------------------------- Sidebar UI ------------------------------- */

  const sidebar = (
    <Sidebar
      calcId={calcId}
      studsKind={studsKind}
      setStudsKind={setStudsKind}
      woodSize={woodSize}
      setWoodSize={setWoodSize}
      spacingWood={spacingWood}
      setSpacingWood={setSpacingWood}
      cavityRSIWood={cavityRSIWood}
      setCavityRSIWood={setCavityRSIWood}
      cavityChoicesWood={rsiOptionsWood[woodSize]}
      steelSize={steelSize}
      setSteelSize={setSteelSize}
      steelSpacingGroup={steelSpacingGroup}
      setSteelSpacingGroup={setSteelSpacingGroup}
      cavityRSISteel={cavityRSISteel}
      setCavityRSISteel={setCavityRSISteel}
      cavityChoicesSteel={rsiOptionsSteel[steelSize]}
      cladding={cladding}
      setCladding={setCladding}
      hardieThickness={hardieThickness}
      setHardieThickness={setHardieThickness}
      hasRainScreen={hasRainScreen}
      setHasRainScreen={setHasRainScreen}
      rainScreenAirGap={rainScreenAirGap}
      setRainScreenAirGap={setRainScreenAirGap}
      sheathingType={sheathingType}
      setSheathingType={setSheathingType}
      osbThickness={osbThickness}
      setOsbThickness={setOsbThickness}
      gwbSheathThickness={gwbSheathThickness}
      setGwbSheathThickness={setGwbSheathThickness}
      codebordSheathThickness={codebordSheathThickness}
      setCodebordSheathThickness={setCodebordSheathThickness}
      hasWeatherBarrier={hasWeatherBarrier}
      setHasWeatherBarrier={setHasWeatherBarrier}
      hasVapourBarrier={hasVapourBarrier}
      setHasVapourBarrier={setHasVapourBarrier}
      interiorGWB={interiorGWB}
      setInteriorGWB={setInteriorGWB}
      woodStrapBehindGWB={woodStrapBehindGWB}
      setWoodStrapBehindGWB={setWoodStrapBehindGWB}
      hasExteriorInsulation={hasExteriorInsulation}
      setHasExteriorInsulation={setHasExteriorInsulation}
      exteriorInsulationType={exteriorInsulationType}
      setExteriorInsulationType={setExteriorInsulationType}
      exteriorCodebordInches={exteriorCodebordInches}
      setExteriorCodebordInches={setExteriorCodebordInches}
      exteriorRainbarrierInches={exteriorRainbarrierInches}
      setExteriorRainbarrierInches={setExteriorRainbarrierInches}
      onCalculate={onCalculate}
      onReset={onReset}
      hasLocation={hasLocation}
      t={t}
    />
  );

  const handleDownload = () => {
    if (!results) return;
    generateResultsPdf({
      calcId,
      province,
      region,
      hdd,
      projectLink: 'https://insulation.owenscorning.ca/builders/calculators/thermal-project-calculator/',
      tableTitle,
      assemblyHeading: t('thermal_calculator.titles.walls_above_grade'),
      tableSubtitle: t('thermal_calculator.effective_thermal_resistance'),
      framingLabel: tableTitle,
      rowsConfig: rowsForTable,
      results,
      t,
    });
  };

  const resultsPane = (
    <div ref={resultsRef}>
      <ResultsBlock
        calcId={calcId}
        province={province}
        region={region}
        hdd={hdd}
        error={error}
        results={results}
        rows={rowsForTable}
      />

      {studsKind === 'steel' && results && (
        <WallAreaFactorsCard structurePercent={results?.AfPercent} cavityPercent={results?.AcPercent} />
      )}

      <Footer results={results} onDownload={handleDownload} />
    </div>
  );

  return <CalculatorLayout sidebar={sidebar} results={resultsPane} />;
};

WallsAboveGrade.propTypes = {
  // supplied by the parent when rendering each calc
  calcId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default connectLocation(WallsAboveGrade);
